import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from './axiosConfig';
import UIIcon from './UIIcon';
import './OperationalReporting.css';

function localISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function defaultDates() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 29);
  return { start: localISODate(start), end: localISODate(end) };
}

function fmtWhen(value, locale) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString(locale);
}

function downloadCsv(filename, headers, rows) {
  const quote = value => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const lines = [headers.map(h => quote(h.label)).join(',')];
  rows.forEach(row => lines.push(headers.map(h => quote(typeof h.value === 'function' ? h.value(row) : row[h.value])).join(',')));
  const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const OperationalReporting = () => {
  const { t, i18n } = useTranslation();
  const isZh = String(i18n.resolvedLanguage || i18n.language || '').toLowerCase().startsWith('zh');
  const locale = isZh ? 'zh-CN' : 'en-US';
  const navigate = useNavigate();
  const initial = useMemo(defaultDates, []);
  const [filters, setFilters] = useState({
    start_date: initial.start,
    end_date: initial.end,
    user_id: '',
    location_ids: [],
    recommendation_status: '',
    search: '',
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [active, setActive] = useState('dsi');

  const fetchReport = useCallback(async (nextFilters = filters) => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      Object.entries(nextFilters).forEach(([key, value]) => {
        if (key === 'location_ids') return;
        if (value !== '' && value != null) params[key] = value;
      });
      if (Array.isArray(nextFilters.location_ids) && nextFilters.location_ids.length > 0) {
        params.location_ids = nextFilters.location_ids.join(',');
      }
      params.limit = 5000;
      const res = await axios.get('reporting/activity/', { params });
      setData(res.data);
    } catch (err) {
      setError(!isZh && err?.response?.data?.detail ? err.response.data.detail : t('reporting.loadError', { defaultValue: 'Unable to load reporting data.' }));
    } finally {
      setLoading(false);
    }
  }, [filters, t, isZh]);

  useEffect(() => {
    fetchReport();
    // Initial load only. Filters are applied explicitly to avoid a request on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

  const clearFilters = () => {
    const d = defaultDates();
    const next = { start_date: d.start, end_date: d.end, user_id: '', location_ids: [], recommendation_status: '', search: '' };
    setFilters(next);
    fetchReport(next);
  };

  const applyQuickRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - Math.max(0, days - 1));
    const next = {
      ...filters,
      start_date: localISODate(start),
      end_date: localISODate(end),
    };
    setFilters(next);
    fetchReport(next);
  };

  const dsi = data?.dsi || { summary: {}, rows: [], by_user: [], by_day: [] };
  const recommendations = data?.recommendations || { summary: {}, rows: [], by_user: [] };
  const filterOptions = data?.filters || { users: [], locations: [], recommendation_statuses: [] };
  const reportScope = data?.scope || {};
  const selectedLocationIds = (filters.location_ids || []).map(String);
  const allAssignedCentersSelected = selectedLocationIds.length === 0;
  const maxUserCount = Math.max(1, ...(dsi.by_user || []).map(x => x.count || 0));

  const toggleLocation = (locationId) => {
    const value = String(locationId);
    const allIds = (filterOptions.locations || []).map(loc => String(loc.id));
    let next;

    if (selectedLocationIds.length === 0) {
      // Empty means "all assigned". Clicking a center starts an explicit subset.
      next = [value];
    } else if (selectedLocationIds.includes(value)) {
      next = selectedLocationIds.filter(id => id !== value);
    } else {
      next = [...selectedLocationIds, value];
    }

    // Selecting every available center is equivalent to "all assigned".
    if (allIds.length > 0 && allIds.every(id => next.includes(id))) next = [];
    updateFilter('location_ids', next);
  };
  const recommendationLabel = (status, fallback = '') => {
    if (status === 'treatment_recommended') return t('recommendation.recommended');
    if (status === 'treatment_not_recommended_at_this_time') return t('recommendation.notRecommended');
    return fallback || '—';
  };
  const sourceLabel = (source) => {
    if (source === 'DSI Analysis') return t('reporting.source.dsi');
    if (source === 'Manufacturing / Order') return t('reporting.source.order');
    return source || '—';
  };

  const exportCurrent = () => {
    if (active === 'dsi') {
      downloadCsv(`dsi-submissions-${filters.start_date || 'all'}-${filters.end_date || 'all'}.csv`, [
        { label: t('reporting.csv.submittedAt'), value: 'submitted_at' },
        { label: t('reporting.patient'), value: 'patient_name' },
        { label: t('reporting.csv.distributorMrn'), value: 'distributors_mrn' },
        { label: t('reporting.location'), value: 'location' },
        { label: t('reporting.submittedBy'), value: 'submitted_by' },
        { label: t('reporting.source'), value: row => sourceLabel(row.source) },
        { label: t('reporting.csv.processingCode'), value: 'processing_code' },
        { label: t('reporting.csv.treatmentRecommendation'), value: row => recommendationLabel(row.treatment_recommendation, row.treatment_recommendation_display) },
        { label: t('reporting.file'), value: 'filename' },
        { label: t('reporting.csv.replacement'), value: row => row.replacement ? t('yes') : t('no') },
        { label: t('reporting.csv.specialCase'), value: row => row.special_case ? t('yes') : t('no') },
        { label: t('reporting.csv.djangoModel'), value: 'database_model' },
        { label: t('reporting.csv.djangoId'), value: 'database_id' },
      ], dsi.rows || []);
    } else {
      downloadCsv(`recommendations-${filters.start_date || 'all'}-${filters.end_date || 'all'}.csv`, [
        { label: t('reporting.csv.recordedAt'), value: 'created_at' },
        { label: t('reporting.patient'), value: 'patient_name' },
        { label: t('reporting.csv.distributorMrn'), value: 'distributors_mrn' },
        { label: t('reporting.location'), value: 'location' },
        { label: t('reporting.recordedBy'), value: 'created_by' },
        { label: t('reporting.recommendation'), value: row => recommendationLabel(row.status, row.status_display) },
        { label: t('reporting.csv.notes'), value: 'notes' },
      ], recommendations.rows || []);
    }
  };

  return (
    <div className="or-page">
      <section className="or-hero">
        <div className="or-hero-copy">
          <div className="or-eyebrow"><UIIcon name="shield" size={13} /> {t('reporting.permissionEyebrow')}</div>
          <h2>{t('reporting.title', { defaultValue: 'DSI & Recommendation Reporting' })}</h2>
          <p>{t('reporting.subtitle', { defaultValue: 'See exactly how many DSI images were submitted, who submitted them, when they were submitted, and every treatment recommendation recorded in Django.' })}</p>
          {data?.generated_at && <small className="or-generated">{t('reporting.lastRefreshed', { date: fmtWhen(data.generated_at, locale) })}</small>}
        </div>
        <button className="or-export" type="button" onClick={exportCurrent} disabled={loading || !data}>
          <UIIcon name="download" size={17} />
          {t('reporting.exportCsv', { defaultValue: 'Export CSV' })}
        </button>
      </section>

      <section className="or-filter-card" aria-label={t('reporting.filters', { defaultValue: 'Report filters' })}>
        <div className="or-filter-head">
          <div><span className="or-filter-icon"><UIIcon name="filter" size={16} /></span><div><strong>{t('reporting.filters', { defaultValue: 'Report filters' })}</strong><small>{t('reporting.refineHint')}</small></div></div>
          <div className="or-quick-ranges" aria-label={t('reporting.quickRanges')}>
            <button type="button" onClick={() => applyQuickRange(1)} disabled={loading}>{t('reporting.rangeToday')}</button>
            <button type="button" onClick={() => applyQuickRange(7)} disabled={loading}>{t('reporting.rangeDays', { count: 7 })}</button>
            <button type="button" onClick={() => applyQuickRange(30)} disabled={loading}>{t('reporting.rangeDays', { count: 30 })}</button>
            <button type="button" onClick={() => applyQuickRange(90)} disabled={loading}>{t('reporting.rangeDays', { count: 90 })}</button>
          </div>
        </div>
        <div className="or-filter-grid">
          <label><span>{t('reporting.startDate', { defaultValue: 'Start date' })}</span><input type="date" value={filters.start_date} onChange={e => updateFilter('start_date', e.target.value)} /></label>
          <label><span>{t('reporting.endDate', { defaultValue: 'End date' })}</span><input type="date" value={filters.end_date} onChange={e => updateFilter('end_date', e.target.value)} /></label>
          <label><span>{t('reporting.user', { defaultValue: 'User' })}</span><select value={filters.user_id} onChange={e => updateFilter('user_id', e.target.value)}><option value="">{t('reporting.allUsers', { defaultValue: 'All users' })}</option>{filterOptions.users.map(u => <option key={u.id} value={u.id}>{u.label}</option>)}</select></label>
          <div className="or-service-center-filter">
            <span className="or-field-label">{t('reporting.serviceCenterScope', { defaultValue: 'Service center scope' })}</span>
            {filterOptions.locations.length === 0 ? (
              <div className="or-center-empty">
                <UIIcon name="shield" size={15} />
                <span>{t('reporting.noAssignedCenters', { defaultValue: 'No service centers are assigned to your account.' })}</span>
              </div>
            ) : filterOptions.locations.length === 1 ? (
              <div className="or-center-locked">
                <UIIcon name="shield" size={15} />
                <div><strong>{filterOptions.locations[0].name}</strong><small>{t('reporting.singleCenterLocked', { defaultValue: 'Reporting is restricted to your assigned service center.' })}</small></div>
              </div>
            ) : (
              <div className="or-center-picker" role="group" aria-label={t('reporting.serviceCenterScope', { defaultValue: 'Service center scope' })}>
                <button
                  type="button"
                  className={`or-center-chip or-center-chip--all ${allAssignedCentersSelected ? 'is-selected' : ''}`}
                  onClick={() => updateFilter('location_ids', [])}
                  disabled={loading}
                >
                  <span className="or-center-check" aria-hidden="true">{allAssignedCentersSelected ? '✓' : ''}</span>
                  {t('reporting.allAssignedCenters', { defaultValue: 'All assigned service centers' })}
                </button>
                {filterOptions.locations.map(loc => {
                  const checked = selectedLocationIds.includes(String(loc.id));
                  return (
                    <button
                      key={loc.id}
                      type="button"
                      className={`or-center-chip ${checked ? 'is-selected' : ''}`}
                      onClick={() => toggleLocation(loc.id)}
                      disabled={loading}
                      aria-pressed={checked}
                    >
                      <span className="or-center-check" aria-hidden="true">{checked ? '✓' : ''}</span>
                      {loc.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <label><span>{t('reporting.recommendation', { defaultValue: 'Recommendation' })}</span><select value={filters.recommendation_status} onChange={e => updateFilter('recommendation_status', e.target.value)}><option value="">{t('reporting.allRecommendations', { defaultValue: 'All recommendations' })}</option>{filterOptions.recommendation_statuses.map(item => <option key={item.value} value={item.value}>{recommendationLabel(item.value, item.label)}</option>)}</select></label>
          <label className="or-search"><span>{t('reporting.search', { defaultValue: 'Search' })}</span><input value={filters.search} onChange={e => updateFilter('search', e.target.value)} placeholder={t('reporting.searchPlaceholder', { defaultValue: 'Patient, MRN, user, location…' })} /></label>
        </div>
        <div className="or-filter-actions">
          <button type="button" className="or-secondary" onClick={clearFilters} disabled={loading}>{t('clear', { defaultValue: 'Clear' })}</button>
          <button type="button" className="or-primary" onClick={() => fetchReport()} disabled={loading}><UIIcon name="refresh" size={16} />{loading ? t('reporting.loading', { defaultValue: 'Refreshing…' }) : t('reporting.apply', { defaultValue: 'Apply filters' })}</button>
        </div>
      </section>

      {data && filterOptions.locations.length > 0 && (
        <div className="or-scope-note">
          <UIIcon name="shield" size={16} />
          <div>
            <strong>{t('reporting.scopeProtected', { defaultValue: 'Service-center access enforced' })}</strong>
            <span>
              {filterOptions.locations.length === 1
                ? t('reporting.scopeSingle', { center: filterOptions.locations[0].name, defaultValue: `This report only includes ${filterOptions.locations[0].name}.` })
                : t('reporting.scopeMultiple', { count: filterOptions.locations.length, defaultValue: `You can report across your ${filterOptions.locations.length} assigned service centers.` })}
            </span>
          </div>
          {reportScope?.mode && <small>{t('reporting.scopeBackend', { defaultValue: 'Enforced by Django' })}</small>}
        </div>
      )}

      {error && <div className="or-error" role="alert">{error}</div>}

      <section className="or-kpis" aria-busy={loading ? 'true' : 'false'}>
        <div className="or-kpi"><span>{t('reporting.dsiSubmissions', { defaultValue: 'DSI submissions' })}</span><strong>{dsi.summary?.total ?? '—'}</strong><small>{t('reporting.dsiPeriod', { defaultValue: 'in selected period' })}</small></div>
        <div className="or-kpi"><span>{t('reporting.submitters', { defaultValue: 'Unique submitters' })}</span><strong>{dsi.summary?.unique_submitters ?? '—'}</strong><small>{t('reporting.today', { defaultValue: '{{count}} today', count: dsi.summary?.today ?? 0 })}</small></div>
        <div className="or-kpi or-kpi--good"><span>{t('recommendation.recommended', { defaultValue: 'Treatment Recommended' })}</span><strong>{recommendations.summary?.current_treatment_recommended ?? '—'}</strong><small>{t('reporting.currentPatients', { defaultValue: 'current patient status' })}</small></div>
        <div className="or-kpi or-kpi--warm"><span>{t('recommendation.notRecommended', { defaultValue: 'Not recommended at this time' })}</span><strong>{recommendations.summary?.current_not_recommended ?? '—'}</strong><small>{t('reporting.currentPatients', { defaultValue: 'current patient status' })}</small></div>
        <div className="or-kpi or-kpi--issue"><span>{t('reporting.needsReview', { defaultValue: 'Submission issues' })}</span><strong>{dsi.summary?.needs_review ?? '—'}</strong><small>{t('reporting.needsReviewHelp', { defaultValue: 'failed or incomplete DSI attempts' })}</small></div>
      </section>

      <div className="or-tabs" role="tablist">
        <button type="button" className={active === 'dsi' ? 'active' : ''} onClick={() => setActive('dsi')}>{t('reporting.dsiTab', { defaultValue: 'DSI Submissions' })}<span>{dsi.total_rows ?? 0}</span></button>
        <button type="button" className={active === 'recommendations' ? 'active' : ''} onClick={() => setActive('recommendations')}>{t('reporting.recTab', { defaultValue: 'Recommendations' })}<span>{recommendations.total_rows ?? 0}</span></button>
      </div>

      {active === 'dsi' ? (
        <div className="or-report-grid">
          <section className="or-panel or-panel--compact">
            <div className="or-panel-head"><div><h3>{t('reporting.bySubmitter', { defaultValue: 'Submissions by user' })}</h3><p>{t('reporting.bySubmitterHelp', { defaultValue: 'Successful DSI processing and manufacturing submissions.' })}</p></div></div>
            <div className="or-bars">
              {(dsi.by_user || []).length === 0 ? <div className="or-empty">{t('reporting.noData', { defaultValue: 'No data for these filters.' })}</div> : (dsi.by_user || []).slice(0, 12).map(item => (
                <div className="or-bar-row" key={item.key}><div className="or-bar-label"><span>{item.key}</span><strong>{item.count}</strong></div><div className="or-bar-track"><div className="or-bar-fill" style={{ width: `${Math.max(4, (item.count / maxUserCount) * 100)}%` }} /></div></div>
              ))}
            </div>
          </section>

          <section className="or-panel or-panel--table">
            <div className="or-panel-head"><div><h3>{t('reporting.dsiDetail', { defaultValue: 'DSI submission detail' })}</h3><p>{t('reporting.dsiDetailHelp', { defaultValue: 'Every row maps back to a Django record for auditability.' })}</p></div><span className="or-row-count">{dsi.total_rows ?? 0}</span></div>
            <div className="or-table-wrap">
              <table className="or-table"><thead><tr><th>{t('reporting.when', { defaultValue: 'When' })}</th><th>{t('reporting.patient', { defaultValue: 'Patient' })}</th><th>{t('reporting.location', { defaultValue: 'Location' })}</th><th>{t('reporting.submittedBy', { defaultValue: 'Submitted by' })}</th><th>{t('reporting.source', { defaultValue: 'Source' })}</th><th>{t('reporting.code', { defaultValue: 'Code' })}</th><th>{t('reporting.recommendation', { defaultValue: 'Recommendation' })}</th><th>{t('reporting.file', { defaultValue: 'File' })}</th><th /></tr></thead><tbody>
                {(dsi.rows || []).map(row => <tr key={row.id}><td className="or-nowrap">{fmtWhen(row.submitted_at, locale)}</td><td><strong>{row.patient_name || '—'}</strong><small>{row.distributors_mrn || row.internal_mrn || ''}</small></td><td>{row.location || '—'}</td><td>{row.submitted_by || t('common.unknown')}</td><td><span className="or-pill">{sourceLabel(row.source)}</span></td><td><span className="or-code">{row.processing_code || '—'}</span></td><td>{row.treatment_recommendation ? <span className={`or-rec-pill ${row.treatment_recommendation === 'treatment_recommended' ? 'is-good' : 'is-warm'}`}>{recommendationLabel(row.treatment_recommendation, row.treatment_recommendation_display)}</span> : '—'}</td><td className="or-file" title={row.filename}>{row.filename || '—'}</td><td><button className="or-link-btn" onClick={() => navigate(`/dashboard/patients/${row.patient_id}/view?tab=files`)}>{t('reporting.open', { defaultValue: 'Open' })}</button></td></tr>)}
              </tbody></table>
              {!loading && (dsi.rows || []).length === 0 && <div className="or-empty or-empty--table">{t('reporting.noDsi', { defaultValue: 'No DSI submissions match these filters.' })}</div>}
            </div>
          </section>
        </div>
      ) : (
        <section className="or-panel or-panel--table">
          <div className="or-panel-head"><div><h3>{t('reporting.recDetail', { defaultValue: 'Recommendation history' })}</h3><p>{t('reporting.recDetailHelp', { defaultValue: 'Every recommendation change is retained with user and timestamp.' })}</p></div><span className="or-row-count">{recommendations.total_rows ?? 0}</span></div>
          <div className="or-table-wrap">
            <table className="or-table"><thead><tr><th>{t('reporting.when', { defaultValue: 'When' })}</th><th>{t('reporting.patient', { defaultValue: 'Patient' })}</th><th>{t('reporting.recommendation', { defaultValue: 'Recommendation' })}</th><th>{t('reporting.recordedBy', { defaultValue: 'Recorded by' })}</th><th>{t('reporting.location', { defaultValue: 'Location' })}</th><th>{t('recommendation.notes', { defaultValue: 'Notes' })}</th><th /></tr></thead><tbody>
              {(recommendations.rows || []).map(row => <tr key={row.id}><td className="or-nowrap">{fmtWhen(row.created_at, locale)}</td><td><strong>{row.patient_name || '—'}</strong><small>{row.distributors_mrn || row.internal_mrn || ''}</small></td><td><span className={`or-rec-pill ${row.status === 'treatment_recommended' ? 'is-good' : 'is-warm'}`}>{recommendationLabel(row.status, row.status_display)}</span></td><td>{row.created_by || t('common.unknown')}</td><td>{row.location || '—'}</td><td className="or-notes">{row.notes || '—'}</td><td><button className="or-link-btn" onClick={() => navigate(`/dashboard/patients/${row.patient_id}/view?tab=files`)}>{t('reporting.open', { defaultValue: 'Open' })}</button></td></tr>)}
            </tbody></table>
            {!loading && (recommendations.rows || []).length === 0 && <div className="or-empty or-empty--table">{t('reporting.noRecommendations', { defaultValue: 'No recommendations match these filters.' })}</div>}
          </div>
        </section>
      )}
    </div>
  );
};

export default OperationalReporting;
